import { addDays, addYears, format, isBefore } from 'date-fns'
import { apiDateFormat } from 'api'
import { getPayForPosition } from '../utils'
import { Location, Position } from 'typings/common_defs'
import { positionTemplateGroups } from './positionTemplateGroups'
import { CompanyWorker } from 'api/worker'

/**
 * Bins pay rate into granular categories matching SQL logic from _pay_rate_by_position.sql
 * @param avgPayRateWithCompanyBonus - The average pay rate with company bonus
 * @returns The pay rate binning category string (e.g., 'a. $9-$10', 'b. $10-$11', etc.)
 */
function getPayRateBinningGranular(avgPayRateWithCompanyBonus: number): string {
  if (avgPayRateWithCompanyBonus < 10) {
    return 'a. $9-$10'
  }
  if (avgPayRateWithCompanyBonus >= 10 && avgPayRateWithCompanyBonus < 11) {
    return 'b. $10-$11'
  }
  if (avgPayRateWithCompanyBonus >= 11 && avgPayRateWithCompanyBonus < 12) {
    return 'c. $11-$12'
  }
  if (avgPayRateWithCompanyBonus >= 12 && avgPayRateWithCompanyBonus < 13) {
    return 'd. $12-$13'
  }
  if (avgPayRateWithCompanyBonus >= 13 && avgPayRateWithCompanyBonus < 14) {
    return 'e. $13-$14'
  }
  if (avgPayRateWithCompanyBonus >= 14 && avgPayRateWithCompanyBonus < 15) {
    return 'f. $14-$15'
  }
  if (avgPayRateWithCompanyBonus >= 15 && avgPayRateWithCompanyBonus < 16) {
    return 'g. $15-$16'
  }
  if (avgPayRateWithCompanyBonus >= 16 && avgPayRateWithCompanyBonus < 17) {
    return 'h. $16-$17'
  }
  if (avgPayRateWithCompanyBonus >= 17 && avgPayRateWithCompanyBonus < 18) {
    return 'i. $17-$18'
  }
  if (avgPayRateWithCompanyBonus >= 18 && avgPayRateWithCompanyBonus < 19) {
    return 'j. $18-$19'
  }
  if (avgPayRateWithCompanyBonus >= 19 && avgPayRateWithCompanyBonus < 20) {
    return 'k. $19-$20'
  }
  if (avgPayRateWithCompanyBonus >= 20 && avgPayRateWithCompanyBonus < 21) {
    return 'l. $20-$21'
  }
  if (avgPayRateWithCompanyBonus >= 21 && avgPayRateWithCompanyBonus < 22) {
    return 'm. $21-$22'
  }
  if (avgPayRateWithCompanyBonus >= 22 && avgPayRateWithCompanyBonus < 23) {
    return 'n. $22-$23'
  }
  if (avgPayRateWithCompanyBonus >= 23 && avgPayRateWithCompanyBonus < 24) {
    return 'o. $23-$24'
  }
  if (avgPayRateWithCompanyBonus >= 24 && avgPayRateWithCompanyBonus < 25) {
    return 'p. $24-$25'
  }
  if (avgPayRateWithCompanyBonus >= 25 && avgPayRateWithCompanyBonus < 26) {
    return 'q. $25-$26'
  }
  if (avgPayRateWithCompanyBonus >= 26 && avgPayRateWithCompanyBonus < 27) {
    return 'r. $26-$27'
  }
  if (avgPayRateWithCompanyBonus >= 27 && avgPayRateWithCompanyBonus < 28) {
    return 's. $27-$28'
  }
  if (avgPayRateWithCompanyBonus >= 28 && avgPayRateWithCompanyBonus < 29) {
    return 't. $28-$29'
  }
  if (avgPayRateWithCompanyBonus >= 29 && avgPayRateWithCompanyBonus < 30) {
    return 'u. $29-$30'
  }
  if (avgPayRateWithCompanyBonus >= 30 && avgPayRateWithCompanyBonus < 31) {
    return 'v. $30-$31'
  }
  if (avgPayRateWithCompanyBonus >= 31 && avgPayRateWithCompanyBonus < 32) {
    return 'w. $31-$32'
  }
  if (avgPayRateWithCompanyBonus >= 32 && avgPayRateWithCompanyBonus < 33) {
    return 'x. $32-$33'
  }
  if (avgPayRateWithCompanyBonus >= 33 && avgPayRateWithCompanyBonus < 34) {
    return 'y. $33-$34'
  }
  return 'z. $34+'
}

export type DemoPulseFillData = {
  date: string
  workersNeeded: number
  workersFilled: number
  fillRate: number
  numHoursWorked: number
  locationId: number
  locationName: string
  numHoursNotice: number
  noticeCategory: string
  payRate: number
  payRateBinning: string
  positionId: number
  positionType: string
  repeatWorkerRate: number
  reliabilityRate: number
  avgWorkerToManagerRating: number
  numWorkersRatedManagerBelow3: number
  avgMarketPayRate: number
  avgShiftToWorkerRating: number
  numShiftToWorkerRatedBelow3: number
  bonusRate: number
  creditRate: number
  compliment: string | null
  complaint: string | null
  workersIds: number[]
}

export function createDemoPulseFillData(
  positions: Position[],
  locations: Location[],
  workers: CompanyWorker[]
): DemoPulseFillData[] {
  const today = new Date()
  const lookbackInYears = 1

  const startDate = addYears(today, -lookbackInYears)
  const endDate = today

  const data: DemoPulseFillData[] = []

  const positionTemplates = positionTemplateGroups.flatMap(
    (group) => group.positionTemplates ?? []
  )

  const positionTemplatesById = positions
    .map((position) => position.positionTemplateId)
    .reduce<Record<number, string>>((acc, positionTemplateId) => {
      if (positionTemplateId) {
        acc[positionTemplateId] =
          positionTemplates.find(
            (template) => template.id === positionTemplateId
          )?.name ?? ''
      }
      return acc
    }, {} as Record<number, string>)

  // create 1 shift per day
  for (let date = startDate; isBefore(date, endDate); date = addDays(date, 1)) {
    // skip day random logic - skips 10% of days
    const shouldSkipDay = Math.random() < 0.1
    if (shouldSkipDay) {
      continue
    }

    const position = positions[Math.floor(Math.random() * positions.length)]
    const positionType = positionTemplatesById[position.positionTemplateId ?? 0]

    if (!positionType) {
      // should not happen unless there's a configuration bug in positions or positionTemplateGroups
      continue
    }

    // numHoursNotice is randomly chosen between 0 and 168 hours (1 week)
    const numHoursNotice = Math.random() * 168

    // Categorize notice based on SQL logic from _pulse_fill_data.sql
    let noticeCategory: string
    let fillRateModifier = 1
    if (numHoursNotice > 72) {
      noticeCategory = '72+ hours'
    } else if (numHoursNotice > 48) {
      noticeCategory = '48-72 hours'
    } else if (numHoursNotice > 24) {
      noticeCategory = '24-48 hours'
      fillRateModifier = 0.8
    } else if (numHoursNotice > 12) {
      noticeCategory = '12-24 hours'
      fillRateModifier = 0.7
    } else if (numHoursNotice > 6) {
      noticeCategory = '6-12 hours'
      fillRateModifier = 0.6
    } else {
      noticeCategory = '0-6 hours'
      fillRateModifier = 0.5
    }

    const workersNeeded = Math.floor(Math.random() * 10) + 5
    const workersFilled = Math.min(
      workersNeeded,
      Math.floor(Math.random() * workersNeeded * fillRateModifier + 5)
    )
    const fillRate = workersFilled / workersNeeded
    const avgShiftDurationInHours = Math.random() * 7 + 3
    const location = locations[Math.floor(Math.random() * locations.length)]
    const payRate = getPayForPosition(position)
    const payRateValue = Number(payRate.payRate ?? payRate.payLumpSum)
    const payRateBinning = getPayRateBinningGranular(payRateValue)
    const repeatWorkerRate = Math.random()
    const reliabilityRate = Math.random() * 0.19 + 0.8 // 80-99%

    // Ensure at least 70% of shifts have ratings between 4-5
    // Track which shifts should have high ratings (4-5) vs low ratings
    const shiftIndex = data.length
    const shouldHaveHighWorkerToManagerRating = shiftIndex % 10 < 7 // 70% of shifts
    const shouldHaveHighShiftToWorkerRating = (shiftIndex + 3) % 10 < 7 // 70% of shifts with offset for independence

    // Calculate avgWorkerToManagerRating: at least 70% between 4-5
    const avgWorkerToManagerRating = shouldHaveHighWorkerToManagerRating
      ? Math.random() * 1 + 4 // 4 to 5
      : Math.random() * 2 + 1 // 1 to 3

    // Calculate avgShiftToWorkerRating: at least 70% between 4-5
    const avgShiftToWorkerRating = shouldHaveHighShiftToWorkerRating
      ? Math.random() * 1 + 4 // 4 to 5
      : Math.random() * 2 + 1 // 1 to 3

    // Track numWorkersRatedManagerBelow3: should be consistent with avgWorkerToManagerRating
    // If avg rating is low (< 3), more workers likely rated below 3
    // If avg rating is high (>= 4), fewer workers rated below 3
    const numWorkersRatedManagerBelow3 =
      avgWorkerToManagerRating < 3
        ? Math.floor(workersFilled * (0.4 + Math.random() * 0.3)) // 40-70% of workers
        : Math.floor(workersFilled * Math.random() * 0.2) // 0-20% of workers

    // Track numShiftToWorkerRatedBelow3: should be consistent with avgShiftToWorkerRating
    const numShiftToWorkerRatedBelow3 =
      avgShiftToWorkerRating < 3
        ? Math.floor(workersFilled * (0.4 + Math.random() * 0.3)) // 40-70% of workers
        : Math.floor(workersFilled * Math.random() * 0.2) // 0-20% of workers

    const avgMarketPayRate = payRateValue * (0.8 + Math.random() * 0.4) // 80-120% of pay rate
    const bonusRate = Math.random() * 0.02 // 0-2%
    const creditRate = -Math.random() * 0.01 // -1% to 0%

    const complimentComplaintRate = Math.random()
    const compliment =
      complimentComplaintRate >= 0.8
        ? [
            'very_productive',
            'great_attitude',
            'above_and_beyond',
            'good_attitude',
            'followed_instructions',
            'productive',
          ][Math.floor(Math.random() * 6)]
        : null
    const complaint =
      complimentComplaintRate <= 0.1
        ? [
            'poor_performance',
            'bad_attitude',
            'not_following_instructions',
            'not_productive',
            'not_reliable',
            'not_team_player',
          ][Math.floor(Math.random() * 6)]
        : null

    // Select a random subset of workers' ids with length workersFilled
    const shuffledWorkers = [...workers].sort(() => Math.random() - 0.5)
    const workersIds = shuffledWorkers
      .slice(0, workersFilled)
      .map((worker) => worker.id)

    data.push({
      date: format(date, apiDateFormat),
      workersNeeded,
      workersFilled,
      fillRate,
      numHoursWorked: workersFilled * avgShiftDurationInHours,
      locationId: location.id,
      locationName: location.name,
      numHoursNotice,
      noticeCategory,
      payRate: payRateValue,
      payRateBinning,
      positionId: position.id,
      positionType,
      repeatWorkerRate,
      reliabilityRate,
      avgWorkerToManagerRating,
      avgMarketPayRate,
      avgShiftToWorkerRating,
      bonusRate,
      creditRate,
      compliment,
      complaint,
      workersIds,
      numWorkersRatedManagerBelow3,
      numShiftToWorkerRatedBelow3,
    } as DemoPulseFillData)
  }

  return data
}
